import { ColumnDef } from '@tanstack/react-table'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClinicUser } from '@magnetar/data-access'
import { clinicUsersService } from '@/services/clinic_users_service'
import { Clinic, User } from '@magnetar/admin-api-interfaces'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useState } from 'react'
import { ClinicUserRoleDialog } from './ClinicUserRoleDialog'
import { EditClinicUserDialog } from './EditClinicUserDialog'
import { DeleteAlert } from '@/components/DeleteAlert'

export const columns: ColumnDef<ClinicUser & { user: User; clinic: Clinic }>[] = [
  {
    accessorKey: 'clinic_user_id',
    header: 'ID',
  },
  {
    accessorKey: 'user.account',
    header: '帳號',
  },
  {
    accessorKey: 'user.user_name',
    header: '名稱',
  },
  {
    accessorKey: 'user.user_role.role_type',
    header: '角色類型',
    cell: ({ row }) => {
      const roleType = row.original.user?.user_role?.role_type
      const roleTypeMap = {
        sys_admin: '系統管理員',
        clinic_admin: '診所管理員',
        supplier_admin: '供應商管理員',
        investor: '投資者',
        custom: '自訂',
      }
      return roleTypeMap[roleType] || roleType
    },
  },
  {
    accessorKey: 'clinic.clinic_name',
    header: '診所名稱',
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const clinicUser = row.original
      const { token } = useAuth()
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showRoleDialog, setShowRoleDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      const handleDelete = async () => {
        if (!token) return

        const result = await clinicUsersService.deleteClinicUser(clinicUser.clinic_user_id, token)

        if (result.success) {
          toast.success('刪除診所使用者成功')
          table.options.meta?.reloadData?.()
        } else {
          toast.error('刪除診所使用者失敗')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                編輯
              </DropdownMenuItem>
              {clinicUser.user?.user_role?.role_type !== 'investor' && (
                <DropdownMenuItem onClick={() => setShowRoleDialog(true)}>
                  角色設定
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-destructive">
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 編輯使用者對話框 */}
          <EditClinicUserDialog
            clinicUser={clinicUser}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={() => table.options.meta?.reloadData?.()}
          />

          {/* 角色設定對話框 */}
          <ClinicUserRoleDialog
            clinicUser={clinicUser}
            open={showRoleDialog}
            onOpenChange={setShowRoleDialog}
            onSuccess={() => table.options.meta?.reloadData?.()}
          />
          

          {/* 刪除確認對話框 */}
          <DeleteAlert
            open={showDeleteAlert}
            onOpenChange={setShowDeleteAlert}
            onConfirm={handleDelete}
            title="確定要刪除此診所使用者嗎？"
            description="此操作無法復原。這將永久刪除此使用者的設定和所有相關資料。"
          />
        </>
      )
    },
  },
]
